import * as React from 'react';
import { memo, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import { PATHS } from 'router/config';
import { ZoomStatus } from 'types';
import {
  useZoomDashboard,
  useZoomAccountUsage,
  useZoomMutations,
  useConditionalPolling,
} from 'core/hooks';
import { useZoomTooShortCountQuery } from 'core/graphql';
import {
  toApiDate,
  yesterdayInWarsaw,
  todayInWarsaw,
} from 'core/utils/zoomTime';

import { Dashboard } from './Dashboard';

const POLL_INTERVAL = 30_000;
/** Backend processing is async (Celery) — re-read counters shortly after. */
const REFETCH_DELAY = 3_000;

export const DashboardContainer = memo(() => {
  const history = useHistory();
  const [range, setRange] = useState<[DateTime, DateTime]>(() => {
    const yesterday = yesterdayInWarsaw();
    return [yesterday, yesterday];
  });

  const {
    data,
    loading,
    refetch,
    startPolling,
    stopPolling,
  } = useZoomDashboard(range);
  const {
    usage,
    loading: usageLoading,
    refetch: refetchUsage,
  } = useZoomAccountUsage();
  const {
    data: tooShortData,
    refetch: refetchTooShort,
  } = useZoomTooShortCountQuery({ fetchPolicy: 'cache-and-network' });
  const mutations = useZoomMutations();

  useConditionalPolling({
    startPolling,
    stopPolling,
    pollInterval: POLL_INTERVAL,
  });

  /** Day-scoped mutations act on the last day of the selected range. */
  const apiDate = toApiDate(range[1]);

  const onQuickYesterdayToday = useCallback(() => {
    setRange([yesterdayInWarsaw(), todayInWarsaw()]);
  }, []);

  const scheduleRefetch = useCallback(() => {
    window.setTimeout(() => {
      refetch();
      refetchUsage();
      refetchTooShort();
    }, REFETCH_DELAY);
  }, [refetch, refetchUsage, refetchTooShort]);

  const onRefreshAll = useCallback(async () => {
    await mutations.refreshAll.run();
    scheduleRefetch();
  }, [mutations.refreshAll, scheduleRefetch]);

  const onSync = useCallback(async () => {
    await mutations.syncRecordings.run(apiDate);
    scheduleRefetch();
  }, [mutations.syncRecordings, apiDate, scheduleRefetch]);

  const onRematch = useCallback(async () => {
    await mutations.runMatching.run(apiDate);
    scheduleRefetch();
  }, [mutations.runMatching, apiDate, scheduleRefetch]);

  const onSendReport = useCallback(async () => {
    await mutations.sendMorningReport.run(apiDate);
  }, [mutations.sendMorningReport, apiDate]);

  const onGoMissing = useCallback(() => {
    history.push(`${PATHS.zoomLessons}?status=${ZoomStatus.NO_RECORDING}`);
  }, [history]);

  const onGoOrphan = useCallback(() => {
    history.push(`${PATHS.zoomRecordings}?orphanOnly=true`);
  }, [history]);

  const onGoFailed = useCallback(() => {
    history.push(`${PATHS.zoomLessons}?status=${ZoomStatus.FAILED}`);
  }, [history]);

  const onGoTooShort = useCallback(() => {
    history.push(`${PATHS.zoomRecordings}?tooShort=true`);
  }, [history]);

  return (
    <Dashboard
      range={range}
      onRangeChange={setRange}
      onQuickYesterdayToday={onQuickYesterdayToday}
      data={data}
      loading={loading}
      usage={usage}
      usageLoading={usageLoading}
      tooShortCount={tooShortData?.allZoomRecordings?.totalCount ?? 0}
      actionsLoading={{
        refreshAll: mutations.refreshAll.loading,
        sync: mutations.syncRecordings.loading,
        rematch: mutations.runMatching.loading,
        report: mutations.sendMorningReport.loading,
      }}
      onRefreshAll={onRefreshAll}
      onSync={onSync}
      onRematch={onRematch}
      onSendReport={onSendReport}
      onGoMissing={onGoMissing}
      onGoOrphan={onGoOrphan}
      onGoFailed={onGoFailed}
      onGoTooShort={onGoTooShort}
    />
  );
});
